import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authHeader } from '@/hooks/useAudit';

/**
 * Hooks for the CO₂ tank rental program (/audit/rentals/*). Everything goes
 * through admin-tank-rentals; nothing here reads a table directly.
 *
 * The three run verbs — build, post, email — are resumable on the server
 * (budgeted at ~18s, `remaining` says how much is left), so `usePostRun` and
 * `useEmailRun` LOOP until remaining is 0 and report progress as they go.
 */

const FN = '/.netlify/functions/admin-tank-rentals';

async function call<T>(init: { method: 'GET'; query: string } | { method: 'POST'; body: Record<string, unknown> }): Promise<T> {
  const headers = await authHeader();
  const res =
    init.method === 'GET'
      ? await fetch(`${FN}?${init.query}`, { headers })
      : await fetch(FN, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(init.body) });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string } & T;
  if (!res.ok || body.ok === false) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}

export interface GasSpec { label: string; itemId: string; itemName: string; defaultRate: number; displayName: string }

export interface Enrollment {
  qbo_customer_id: string;
  enrolled: boolean;
  rate_overrides: Record<string, number>;
  /** Tanks billed for a gas while its count is negative (set 2026-09-23 for the accounts negative then; cleared by an audit). Empty = a negative holds. */
  negative_floors?: Record<string, number>;
  email_to: string[];
  email_enabled: boolean;
  notes: string | null;
  source: string | null;
  enrolled_at: string | null;
  enrolled_by: string | null;
  unenrolled_at: string | null;
  unenrolled_by: string | null;
}

export interface RosterRow {
  qbo_customer_id: string;
  customer_name: string;
  qbo_active: boolean | null;
  qbo_email: string | null;
  portal_customer_id: string | null;
  enrolled: boolean;
  enrollment: Enrollment | null;
  deliveries_12mo: number;
  last_delivery: string | null;
  last_rental_doc: string | null;
  last_rental_date: string | null;
  gases: Array<{ label: string; on_hand: number }> | null;
}

export interface PlannedLine {
  label: string; display_name: string; qbo_item_id: string; item_name: string;
  prev: number; deliveries: number; pickups: number; qty: number; unit_price: number; amount: number; description: string;
  /** Present when this gas was billed at the 4-tank minimum over a NEGATIVE count (the value is that count). */
  audit_floor_from?: number;
}
export interface ComputeRow {
  label: string; prev_balance: number; deliveries: number; pickups: number; new_balance: number;
  last_rate: number | null; anchor_doc: string | null; anchor_date: string | null; last_activity: string | null;
}
export interface Plan { status: 'draft' | 'held' | 'skipped'; reason: string | null; lines: PlannedLine[]; total: number; warnings: string[]; audit_floor?: string[] }

export type InvoiceStatus = 'draft' | 'held' | 'skipped' | 'posting' | 'posted' | 'failed';
export interface RentalInvoiceRow {
  id: string; period: string; qbo_customer_id: string; customer_name: string | null; doc_number: string; txn_date: string;
  due_date: string | null; lines: PlannedLine[]; total: number; status: InvoiceStatus; reason: string | null; warnings: string[] | null;
  qbo_invoice_id: string | null; qbo_total: number | null; error: string | null; posted_at: string | null; posted_by: string | null;
  email_status: 'pending' | 'sent' | 'failed' | 'none'; email_to: string[]; emailed_at: string | null; email_error: string | null;
}
export interface RunRow {
  period: string; status: 'draft' | 'posting' | 'posted'; built_at: string | null; built_by: string | null;
  posted_at: string | null; posted_by: string | null; notified_at: string | null; summary: Record<string, unknown>;
}
export interface RunSummary { enrolled: number; draft: number; held: number; skipped: number; posted: number; failed: number; total_draft: number; total_posted: number; warnings: number }

export function useTankRentalRoster() {
  return useQuery<{ roster: RosterRow[]; gases: GasSpec[] }>({
    queryKey: ['tank-rentals', 'roster'],
    queryFn: () => call({ method: 'GET', query: 'view=roster' }),
    staleTime: 30_000,
  });
}

export function useTankRentalCustomer(qboId: string | null, period: string) {
  return useQuery<{ period: string; rows: ComputeRow[]; plan: Plan; doc_number: string; enrollment: Enrollment | null; roster: RosterRow | null; gases: GasSpec[] }>({
    queryKey: ['tank-rentals', 'customer', qboId, period],
    queryFn: () => call({ method: 'GET', query: `view=customer&qbo_id=${encodeURIComponent(qboId ?? '')}&period=${period}` }),
    enabled: !!qboId,
  });
}

export function useTankRentalRun(period: string) {
  return useQuery<{ period: string; run: RunRow | null; invoices: RentalInvoiceRow[] }>({
    queryKey: ['tank-rentals', 'run', period],
    queryFn: () => call({ method: 'GET', query: `view=run&period=${period}` }),
  });
}

export interface EstimateRow {
  qbo_customer_id: string; customer_name: string; qbo_active: boolean | null; doc_number: string;
  status: Plan['status']; reason: string | null; lines: PlannedLine[]; total: number; warnings: string[]; rows: ComputeRow[];
}
export interface Estimate { period: string; computed_at: string; customers: EstimateRow[]; total: number; billable: number; held: number; zero: number }

/** Every enrolled customer's would-be invoice for `period`, from today's counts. Writes nothing. */
export function useTankRentalEstimate(period: string | null) {
  return useQuery<Estimate>({
    queryKey: ['tank-rentals', 'estimate', period],
    queryFn: () => call({ method: 'GET', query: period ? `view=estimate&period=${period}` : 'view=estimate' }),
    staleTime: 60_000,
  });
}

export interface HistoryLedgerRow {
  kind: 'rental' | 'delivery' | 'pickup' | 'adjustment' | 'system_adjustment';
  label: string; txn_date: string; qbo_invoice_id: string | null; doc_number: string | null;
  quantity: number; prev_balance: number | null; unit_price: number | null; amount: number | null;
  item_name: string | null; description: string | null;
}
export interface HistoryAuditCount { label: string; system_count: number; counted: number; delta: number }
export interface HistoryAudit {
  id: string; doc_number: string; status: string; created_at: string; signed_name: string; driver_name: string | null;
  manager_name: string | null; has_adjustment: boolean; qbo_invoice_id: string | null; approved_by_email: string | null;
  approved_at: string | null; notes: string | null; counts: HistoryAuditCount[]; final_counts: HistoryAuditCount[] | null;
  photos: Array<{ url: string; path: string }>; signature_url: string | null;
}
export interface HistoryAlert {
  id: string; label: string; cause: string | null; on_hand_at_alert: number | null; first_doc_number: string | null;
  first_date: string | null; alerted_at: string | null; resolved_at: string | null; resolved_reason: string | null; resolved_note: string | null;
}
export interface CustomerHistory {
  qbo_customer_id: string; portal_customer_id: string | null; months: number;
  ledger: HistoryLedgerRow[]; audits: HistoryAudit[]; alerts: HistoryAlert[];
}

/** One account's cylinder ledger + its audits (signed photo URLs, 10-min TTL) + count alerts. */
export function useTankRentalHistory(qboId: string | null, months = 24) {
  return useQuery<CustomerHistory>({
    queryKey: ['tank-rentals', 'history', qboId, months],
    queryFn: () => call({ method: 'GET', query: `view=history&qbo_id=${encodeURIComponent(qboId ?? '')}&months=${months}` }),
    enabled: !!qboId,
    // the photo links expire in 10 minutes; refetch a little before that
    staleTime: 8 * 60_000,
  });
}

export function useTankRentalRuns() {
  return useQuery<{ runs: RunRow[] }>({
    queryKey: ['tank-rentals', 'runs'],
    queryFn: () => call({ method: 'GET', query: 'view=runs' }),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['tank-rentals'] });
}

export function useSetEnrollment() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (v: { qbo_customer_id: string; enrolled: boolean; notes?: string | null }) =>
      call<{ enrollment: Enrollment }>({ method: 'POST', body: { action: 'set_enrollment', source: 'audit_portal', ...v } }),
    onSettled: inv,
  });
}

export function useUpdateSettings() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (v: { qbo_customer_id: string; rate_overrides?: Record<string, number>; negative_floors?: Record<string, number>; email_to?: string[]; email_enabled?: boolean; notes?: string | null }) =>
      call<{ enrollment: Enrollment }>({ method: 'POST', body: { action: 'update_settings', ...v } }),
    onSettled: inv,
  });
}

export function useBuildRun() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (period: string) => call<{ summary: RunSummary; invoices: RentalInvoiceRow[] }>({ method: 'POST', body: { action: 'build_run', period } }),
    onSettled: inv,
  });
}

export interface LoopProgress { posted: number; failed: number; sent: number; skipped: number; remaining: number; rounds: number }

/** Post until nothing is left, at most 40 rounds (~12 minutes). */
export function usePostRun(onProgress?: (p: LoopProgress) => void) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (v: { period: string; only?: string[]; retry_failed?: boolean }) => {
      const p: LoopProgress = { posted: 0, failed: 0, sent: 0, skipped: 0, remaining: 0, rounds: 0 };
      for (let i = 0; i < 40; i++) {
        const r = await call<{ posted: number; failed: number; remaining: number }>({ method: 'POST', body: { action: 'post_run', ...v } });
        p.posted += r.posted; p.failed += r.failed; p.remaining = r.remaining; p.rounds += 1;
        onProgress?.({ ...p });
        if (r.remaining === 0 || (r.posted === 0 && r.failed === 0)) break;
      }
      return p;
    },
    onSettled: inv,
  });
}

export function useEmailRun(onProgress?: (p: LoopProgress) => void) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (v: { period: string; only?: string[] }) => {
      const p: LoopProgress = { posted: 0, failed: 0, sent: 0, skipped: 0, remaining: 0, rounds: 0 };
      for (let i = 0; i < 40; i++) {
        const r = await call<{ sent: number; failed: number; skipped: number; remaining: number }>({ method: 'POST', body: { action: 'email_run', ...v } });
        p.sent += r.sent; p.failed += r.failed; p.skipped += r.skipped; p.remaining = r.remaining; p.rounds += 1;
        onProgress?.({ ...p });
        if (r.remaining === 0 || (r.sent === 0 && r.failed === 0 && r.skipped === 0)) break;
      }
      return p;
    },
    onSettled: inv,
  });
}

export function useHoldInvoice() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (v: { invoice_id: string; hold: boolean; reason?: string }) =>
      call<{ invoice: RentalInvoiceRow }>({ method: 'POST', body: { action: v.hold ? 'hold' : 'unhold', invoice_id: v.invoice_id, reason: v.reason ?? null } }),
    onSettled: inv,
  });
}

/** YYYY-MM-01 for the month containing `d` (local time is fine for a picker). */
export function periodFor(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y!, (m ?? 1) - 1 + months, 1);
  return periodFor(d);
}
export function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
export const fmtMoney = (n: number) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;
